import { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Clock, ShieldOff, Layers } from 'lucide-react';

/**
 * Org-level umbrella license warning banner.
 *
 * Hidden entirely when:
 *   - the org has no OrgLicense (backward compatible)
 *   - license is healthy (active, not expiring soon, no module over 80%)
 *
 * Shows above the existing CcmLicenseBanner; both can render together when
 * tenant + org level issues coexist.
 */
const FETCH_PATH = '/api/org-license';
const REFRESH_MS = 5 * 60 * 1000;
const NEAR_EXPIRY_DAYS = 30;

const OrgLicenseBanner = () => {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(FETCH_PATH, { credentials: 'include' });
      if (!res.ok) {
        setStatus(null);
        return;
      }
      const data = await res.json();
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  if (!status || !status.hasLicense || dismissed) return null;

  // Decide what to show in priority order: revoked > expired > near-expiry > over-limit.
  const moduleOverLimit = Object.entries(status.modules || {}).find(([, m]) =>
    m && m.enabled && typeof m.limit === 'number' && m.limit > 0 && (m.used ?? 0) / m.limit >= 0.8
  );

  let level = null;
  let icon = AlertTriangle;
  let message = '';

  if (status.status === 'REVOKED') {
    level = 'expired';
    icon = ShieldOff;
    message = 'Your organization license has been revoked. Contact your account manager.';
  } else if (status.status === 'SUSPENDED') {
    level = 'expired';
    icon = ShieldOff;
    message = 'Your organization license is suspended. Contact your account manager.';
  } else if (status.status === 'EXPIRED' || status.daysUntilExpiry < 0) {
    level = 'expired';
    icon = AlertTriangle;
    const dt = status.expiresAt ? new Date(status.expiresAt).toLocaleDateString() : '';
    message = `Your organization license expired${dt ? ` on ${dt}` : ''}.`;
  } else if (status.daysUntilExpiry !== undefined && status.daysUntilExpiry <= NEAR_EXPIRY_DAYS) {
    level = 'warning';
    icon = Clock;
    message = `Your organization license expires in ${status.daysUntilExpiry} day${status.daysUntilExpiry === 1 ? '' : 's'}.`;
  } else if (moduleOverLimit) {
    const [moduleKey, m] = moduleOverLimit;
    const pct = Math.round((m.used / m.limit) * 100);
    level = 'warning';
    icon = Layers;
    const label = moduleKey.replace(/_/g, ' ');
    message = `${label} is at ${pct}% of your license capacity (${m.used} of ${m.limit}).`;
  }

  if (!level) return null;

  const Icon = icon;
  return (
    <div className={`md-license-banner ${level}`} data-org-license="true">
      <div className="md-license-banner-content">
        <Icon size={18} />
        <span>{message}</span>
      </div>
      <button
        className="md-license-banner-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default OrgLicenseBanner;
